import React, { useState, useEffect, type MouseEvent } from 'react';
import ChatMessage from './components/ChatMessage';
import ChatInputField from './components/ChatInputField';
import classNames from 'classnames';
import FiltersAssistantController from './FiltersAssistantController';
import {
  StartControllerScope,
  useController,
  ApplicationView as view,
} from '@aha-app/mvc';

import styles from './AccountSearchAiChat.mod.less';

interface AccountSearchAiChatProps {
  cache: { collapsed: boolean };
}

const ControlledAccountSearchAiChat = StartControllerScope(
  FiltersAssistantController,
  view(AccountSearchAiChat)
);

export default ControlledAccountSearchAiChat;

function AccountSearchAiChat({ cache }: AccountSearchAiChatProps) {
  const [responseCollapsed, setResponseCollapsed] = useState(cache.collapsed);
  const {
    isLoading,
    isDebouncing,
    messagesToDisplay,
    sendMessage,
    show,
    onExpandChat,
    onResultClick,
  } = useAccountSearchAiChat();

  useEffect(() => {
    cache.collapsed = responseCollapsed;
  }, [cache, responseCollapsed]);

  useEffect(() => {
    if (messagesToDisplay.length === 0) setResponseCollapsed(true);
  }, [messagesToDisplay.length]);

  if (!show) return null;

  const setCollapsed = (collapsed: boolean) => {
    setResponseCollapsed(prevValue => {
      if (prevValue && !collapsed) onExpandChat();

      return collapsed;
    });
  };

  const firstResponseIsLoading = !messagesToDisplay.length || isDebouncing;
  const draftingMessage = getLoadingMessage({
    isLoading: isLoading || isDebouncing,
    firstResponseIsLoading,
  });

  const containerStyles = classNames(styles.container, {
    [styles.containerCollapsed]: responseCollapsed,
    'tw-grid tw-transition-[grid-template-rows]': true,
    'tw-duration-300 tw-grid-rows-[0fr]': true,
    'tw-grid-rows-[1fr]': !responseCollapsed,
  });

  return (
    <>
      <ExpandTransitionWrapper disabled={!!messagesToDisplay.length}>
        <div className={containerStyles} onClick={() => setCollapsed(false)}>
          <div
            className={`tw-overflow-hidden tw-min-h-[120px] tw-gap-5 tw-flex tw-flex-col`}
          >
            {messagesToDisplay.map((message, index) => (
              <React.Fragment key={message.role + index}>
                <ChatMessage
                  message={message}
                  showDisclaimer={index === 0}
                  onResultClick={onResultClick}
                />
              </React.Fragment>
            ))}

            {draftingMessage && <DraftingMessage text={draftingMessage} />}

            <div className='tw-mb-12'>
              <ChatInputField
                isFetching={isLoading}
                fetchResponse={sendMessage}
                autofocus={!responseCollapsed}
              />
            </div>

            <ToggleButton
              onClick={() => setCollapsed(!responseCollapsed)}
              collapsed={responseCollapsed}
            />
          </div>
        </div>
      </ExpandTransitionWrapper>
      <div className='h-600 tw-px-5 tw-pt-5'>Keyword results</div>
    </>
  );
}

function ExpandTransitionWrapper({
  children,
  disabled,
}: {
  children: JSX.Element;
  disabled: boolean;
}) {
  const [initialized, setInitialized] = useState(disabled);

  useEffect(() => {
    // This timeout is here because using react-easy-state as a wrapper means we have more variability when it comes to render-cycle timing.
    // We can't guarantee when the contents are re-rendered, so we need to wait a bit before starting any transition.
    const timeoutId = setTimeout(() => setInitialized(true), 5);
    return () => clearTimeout(timeoutId);
  }, []);

  const outerClasses = classNames({
    'tw-grid-rows-[1fr]': initialized,
  });

  return (
    <div
      className={
        'tw-grid tw-transition-[grid-template-rows] tw-duration-300 tw-grid-rows-[0fr] ' +
        outerClasses
      }
    >
      <div className={'tw-overflow-hidden'}>{children}</div>
    </div>
  );
}

function DraftingMessage({ text }: { text: string }) {
  const classes = classNames(styles.draftingMessageText);
  return (
    <div className={styles.assistantChatMessage}>
      <aha-ai-icon className='tw-pt-[2px]' />
      <div className={classes}>{text}</div>
    </div>
  );
}

function ToggleButton({
  onClick,
  collapsed,
}: {
  onClick: () => void;
  collapsed: boolean;
}) {
  const iconType = collapsed ? 'fa-chevron-down' : 'fa-chevron-up';
  const toggleText = collapsed ? 'Show more' : 'Show less';
  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <aha-button kind='plain' class={styles.responseToggle} onClick={toggle}>
      {toggleText}
      <i className={`fa-solid ${iconType} fa-fw`} />
    </aha-button>
  );
}

function useAccountSearchAiChat() {
  const controller = useController<FiltersAssistantController>();
  const { isLoading, data, isDebouncing, isQueryActionable } = controller.state;
  const hasContent =
    (isQueryActionable && isDebouncing) || isLoading || !!data.length;

  return {
    isLoading,
    isDebouncing,
    show: hasContent,
    messagesToDisplay: data.slice(1),
    sendMessage: controller.actionSendMessage,
    onExpandChat: controller.actionTrackExpandChat,
    onResultClick: controller.actionResultClick,
  };
}

function getLoadingMessage({
  isLoading,
  firstResponseIsLoading,
}: {
  isLoading: boolean;
  firstResponseIsLoading: boolean;
}): string {
  if (firstResponseIsLoading) return 'AI is trying to help';
  if (isLoading) return 'Searching';

  return '';
}
